import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Software } from 'models';
import * as routes from 'router/Routes';
import Card from 'components/UI/Card';
import { ContentContainer, NameContainer, SubTitle } from './styled';
import {
  Rollout,
  RolloutFilter,
  RolloutFilterAttribute,
  RolloutList,
} from 'models/rollout';
import SortableTable from 'components/UI/Table/SortableTable';
import Loader from 'components/UI/Loader';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { fetchRolloutList, rolloutListSelector } from 'store';
import Pagination, { PagePagination } from 'components/UI/Pagination';
import Button from 'components/UI/Button';
import { CardHeaderTop, CardHeaderTopColumn } from 'pages/Software/styled';
import Heading, { Type as HeadingType } from 'components/UI/Heading';
import { useAccess } from 'hooks/useAccess';
import CreateRolloutModal from '../CreateRolloutModal';

type Props = {
  software: Software | null;
};

const headerCells = [
  {
    title: 'rollout.name',
    sortable: false,
  },
  {
    title: 'rollout.status',
    sortable: false,
  },
];

// Initial pagination
const initialPagination = {
  limit: 5,
  offset: 0,
  page: 1,
};

export type TableRollouts = {
  name: string;
  status: string;
  link: string;
};

const RolloutCard: FC<Props> = ({ software }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { createRolloutAccess } = useAccess();
  // State
  const [pagination, setPagination] =
    useState<PagePagination>(initialPagination);
  const {
    data: rollouts,
    isLoading,
    totalCount,
  }: RolloutList = useAppSelector(rolloutListSelector);

  const fetchRollouts = useCallback(
    (rolloutFilters: RolloutFilter[], offset: number, limit: number) => {
      dispatch(fetchRolloutList(rolloutFilters, offset, limit));
    },
    [dispatch]
  );

  const [openCreateRolloutModal, setOpenCreateRolloutModal] =
    useState<boolean>(false);

  const onOpen = useCallback(() => {
    setOpenCreateRolloutModal(true);
  }, [setOpenCreateRolloutModal]);

  const onClose = useCallback(() => {
    setOpenCreateRolloutModal(false);
    fetchRollouts(
      [
        {
          attribute: RolloutFilterAttribute.SOFTWARE_ID,
          value: software?.id || '0',
        },
      ],
      0,
      0
    );
  }, [setOpenCreateRolloutModal, fetchRollouts, software]);

  useEffect(() => {
    if (software?.id) {
      dispatch(
        fetchRolloutList(
          [
            {
              attribute: RolloutFilterAttribute.SOFTWARE_ID,
              value: software?.id,
            },
          ],
          initialPagination.offset,
          initialPagination.limit
        )
      );
    }
  }, [dispatch, software]);

  const onClick = useCallback(
    (name: string) => {
      if (name) {
        dispatch(
          fetchRolloutList(
            [{ attribute: RolloutFilterAttribute.NAME, value: name }],
            0
          )
        );
      }
      navigate(`${routes.ROLLOUTS}`);
    },
    [dispatch, navigate]
  );

  const onSetPagination = useCallback(
    (paginate: PagePagination) => {
      setPagination(paginate);
      fetchRollouts(
        [
          {
            attribute: RolloutFilterAttribute.SOFTWARE_ID,
            value: software?.id || '0',
          },
        ],
        paginate.offset,
        paginate.limit
      );
    },
    [fetchRollouts, software]
  );

  const rolloutTable = useMemo(() => {
    if (isLoading) {
      return <Loader center />;
    }
    if (!software || !rollouts) {
      return null;
    }

    const tableContent: TableRollouts[] = rollouts.map((rollout: Rollout) => {
      return {
        name: rollout.attributes.name || '–',
        status: rollout.attributes.status || '-',
        link: rollout.attributes.name || '–',
      };
    });
    if (tableContent.length === 0) {
      return null;
    }
    return (
      <SortableTable
        headerCells={headerCells}
        bodyRows={tableContent}
        onClick={onClick}
      />
    );
  }, [software, rollouts, isLoading, onClick]);

  return (
    <Card>
      <CardHeaderTop>
        <CardHeaderTopColumn>
          <Heading type={HeadingType.h3} uppercase>
            <FormattedMessage id="software.rollout" />
          </Heading>
        </CardHeaderTopColumn>
        <CardHeaderTopColumn>
          {createRolloutAccess && (
            <Button
              id="open_create_rollout_btn"
              backgroundColor="transparent"
              color="primary"
              onClick={onOpen}
            >
              <FormattedMessage id="rollout.btn_create" />
            </Button>
          )}
        </CardHeaderTopColumn>
      </CardHeaderTop>
      <SubTitle>
        <FormattedMessage id="software.fota" />
      </SubTitle>
      <ContentContainer>
        <NameContainer>
          {rolloutTable}
          <Pagination
            totalCount={totalCount}
            noItems={rollouts?.length || 0}
            pagination={pagination}
            setPagination={onSetPagination}
          />
        </NameContainer>
      </ContentContainer>
      <CreateRolloutModal        
        open={openCreateRolloutModal}
        onClose={onClose}
        software={software}
      />
    </Card>
  );
};

export default RolloutCard;
